import {
  FileText,
  ScanEye,
  Copy,
  Network,
  ChartColumn,
  Cloud,
  Search,
  type LucideIcon,
} from "lucide-react";
import type { ToolId } from "../toolExperience/types";
export interface ToolDefinition {
  id: ToolId;
  label: string;
  description: string;
  path: string;
  icon: LucideIcon;
  group: "Plan and create" | "Audit and compare" | "Track and measure";
  keywords: string[];
  availability: "available" | "coming-soon";
  startAction?: { label: string; href: string; blockedRoles?: string[] };
  allowedRoles?: string[];
}
export const toolDefinitions: readonly ToolDefinition[] = [
  {
    id: "briefings",
    label: "Briefings",
    description: "Research topics and create content briefs.",
    path: "/briefings",
    icon: FileText,
    group: "Plan and create",
    keywords: ["brief", "content", "seo", "outline", "research"],
    availability: "available",
    startAction: { label: "New brief", href: "/briefings?start=brief" },
  },
  {
    id: "monitoring",
    label: "Monitoring",
    description: "Track website changes and monitoring health.",
    path: "/monitoring",
    icon: ScanEye,
    group: "Track and measure",
    keywords: ["monitor", "website", "changes", "alerts"],
    availability: "available",
    startAction: { label: "Monitor website", href: "/monitoring?create=1" },
  },
  {
    id: "similarity",
    label: "Similarity",
    description: "Find similar and duplicate content across pages.",
    path: "/similarity",
    icon: Copy,
    group: "Audit and compare",
    keywords: ["duplicate", "comparison", "excel", "sitemap", "jobs"],
    availability: "available",
    startAction: {
      label: "Run comparison",
      href: "/similarity?start=comparison",
    },
  },
  {
    id: "clustering",
    label: "Clustering",
    description: "Group keywords into content opportunities.",
    path: "/clustering",
    icon: Network,
    group: "Plan and create",
    keywords: ["cluster", "keywords", "serp"],
    availability: "available",
    startAction: {
      label: "Cluster keywords",
      href: "/clustering?start=clustering",
      blockedRoles: ["viewer"],
    },
  },
  {
    id: "analytics",
    label: "Analytics",
    description: "Explore traffic and search performance.",
    path: "/analytics",
    icon: ChartColumn,
    group: "Track and measure",
    keywords: ["traffic", "google", "ga4", "gsc", "performance"],
    availability: "available",
  },
  {
    id: "wordcloud",
    label: "Word Cloud",
    description: "Explore words and content patterns on a website.",
    path: "/wordcloud",
    icon: Cloud,
    group: "Audit and compare",
    keywords: ["words", "crawl", "content", "cloud"],
    availability: "available",
  },
  {
    id: "competitor-analysis",
    label: "Competitor Analysis",
    description: "Compare competitor content and positioning.",
    path: "/competitor-analysis",
    icon: Search,
    group: "Audit and compare",
    keywords: ["competitor", "competition"],
    availability: "coming-soon",
  },
];
export function getAvailableTools(role?: string) {
  return toolDefinitions.filter(
    (t) =>
      t.availability === "available" &&
      (!t.allowedRoles || (!!role && t.allowedRoles.includes(role))),
  );
}
export function canStartTool(tool: ToolDefinition, role?: string) {
  return (
    tool.availability === "available" &&
    !!tool.startAction &&
    !tool.startAction.blockedRoles?.includes(role || "")
  );
}
export function searchTools(query: string, role?: string) {
  const words = query.trim().toLocaleLowerCase().split(/\s+/);
  return getAvailableTools(role).filter((t) =>
    words.every((word) =>
      `${t.label} ${t.description} ${t.keywords.join(" ")}`
        .toLocaleLowerCase()
        .includes(word),
    ),
  );
}
